import { useState, useEffect } from 'react';
import axios from 'axios';

export const useTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTemplates = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/email-templates`);
      setTemplates(response.data);
    } catch (err) {
      setError('Failed to fetch templates');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
  };

  const handleUpdateTemplate = async (updatedTemplate) => {
    setIsLoading(true);
    setError(null);
    try {
      const templateData = {
        name: updatedTemplate.name,
        subject: updatedTemplate.subject,
        content: updatedTemplate.content,
        pdfAttachment: {
          name: updatedTemplate.pdfAttachment?.name || '',
          url: updatedTemplate.pdfAttachment?.url || ''
        }
      };

      const response = await axios.put(`${process.env.REACT_APP_API_URL}/email-templates/${updatedTemplate._id}`, templateData);

      setTemplates(prevTemplates =>
        prevTemplates.map(template =>
          template._id === updatedTemplate._id ? response.data : template
        )
      );
      setEditingTemplate(null);
      return { success: true, error: null };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to update template';
      setError(errorMessage);
      console.error(err);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplate = async (newTemplate) => {
    setIsLoading(true);
    setError(null);
    try {
      const templateData = {
        name: newTemplate.name,
        subject: newTemplate.subject,
        content: newTemplate.content,
        pdfAttachment: newTemplate.pdfAttachment ? {
          name: newTemplate.pdfAttachment.name || '',
          url: newTemplate.pdfAttachment.url || ''
        } : null
      };

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/email-templates`, templateData);

      setTemplates(prevTemplates => [...prevTemplates, response.data]);
      return { success: true, error: null };
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create template';
      setError(errorMessage);
      console.error(err);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    setIsLoading(true);
    setError(null);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/email-templates/${templateId}`);
      setTemplates(prevTemplates => prevTemplates.filter(template => template._id !== templateId));
    } catch (err) {
      setError('Failed to delete template');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    templates,
    editingTemplate,
    isLoading,
    error,
    handleEditTemplate,
    handleUpdateTemplate,
    handleCreateTemplate,
    handleDeleteTemplate,
  };
};